/**
 * `StatCard` — LA carte de statistique du produit (LOT-H4).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE FICHIER EXISTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * L'audit visuel du 2026-08 (§6.1) a inventorié **11 variantes de carte KPI**
 * (A → K) sur 33 écrans : tuile + label à droite, label seul, valeur colorée par
 * carte, fond entièrement teinté, accent cyan en haut, icône en cercle… Aucune
 * page n'en partageait le gabarit avec une autre. Ce composant les remplace
 * toutes, et il est le SEUL gabarit autorisé pour une valeur chiffrée mise en
 * avant.
 *
 * Voir `docs/design/DESIGN-SYSTEM.md` §5bis — ce fichier applique le contrat,
 * il ne le décide pas.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LES DEUX RÈGLES QUI TIENNENT LE RESTE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * 1. **AUCUNE variante de couleur par carte.** Colorer cinq cartes en bleu /
 *    ambre / vert / violet parce qu'il en fallait cinq est exactement ce que le
 *    §1.2 du design system interdit. La tuile d'icône est TOUJOURS `primary`
 *    teinté ; la seule couleur qui porte du sens est celle du **delta**.
 *
 * 2. **Une BANDE, pas un panneau.** Amendement utilisateur ratifié le
 *    2026-08-05 : les cartes d'avant faisaient 110 à 150 px de haut et
 *    mangeaient le premier écran d'un iPad. Celle-ci fait **68 px** (82 px quand
 *    le libellé se replie sur deux lignes). Une rangée de KPI se lit d'un coup
 *    d'œil au-dessus du contenu ; elle ne s'y substitue pas.
 */
import type { LucideIcon } from 'lucide-react'
import { Children, type ReactNode } from 'react'

/**
 * Variation affichée à droite de la valeur.
 *
 * `value` est **déjà formatée** par l'appelant (`'+12 %'`, `'2:41'`,
 * `formatCurrency(...)`) : la carte n'a aucune opinion sur les formats, c'est
 * `utils/format.ts` qui les détient (design system §6).
 */
export interface StatCardDelta {
  /** Texte de la variation, déjà formaté. */
  value: string
  /** Sens de la flèche : `up` → ↑, `down` → ↓. */
  direction: 'up' | 'down'
  /**
   * Couleur du delta. Par défaut `up` = succès et `down` = danger.
   *
   * L'échappatoire existe pour les métriques où **monter est une mauvaise
   * nouvelle** — un coût, un taux d'abandon, des appels en échec. Sans elle, un
   * budget en hausse s'afficherait en vert, ce qui est un contresens métier.
   * C'est la SEULE option de couleur de tout le composant.
   */
  tone?: 'success' | 'danger'
}

export interface StatCardProps {
  /**
   * Libellé court, en capitales (`text-t6`). Passe par i18next.
   *
   * `ReactNode` et non `string` depuis RT-DA2 (DA-055) : une UNITÉ contenue
   * dans le libellé doit pouvoir échapper à la capitalisation (« (<10 s) » se
   * lisait « (<10S) », où `S` est le siemens) via un `<span class="normal-case">`.
   */
  label: ReactNode
  /**
   * Valeur affichée, **déjà formatée**. Une durée passe par `formatDuration`,
   * un montant par `formatCurrency` : jamais de `toFixed` ni de
   * `toLocaleString` dans la page (design system §6).
   */
  value: string | number
  /** Icône Lucide, rendue à 20 px dans une tuile teintée. Optionnelle. */
  icon?: LucideIcon
  /** Variation optionnelle, inline à droite de la valeur. */
  delta?: StatCardDelta
  /**
   * Précision neutre à droite de la valeur, **déjà formatée** (RT-DONS, R-3 :
   * « · 15 août 2026 » après le montant du dernier don).
   *
   * Même fente que `delta`, sans flèche ni couleur sémantique : dans la
   * colonne la plus étroite du produit (179 px), un complément CONCATÉNÉ à la
   * valeur serait tronqué par le `truncate` — mesuré : « 31,00 € · 15 août
   * 2026 » fait 214 px pour 147 px disponibles à 4 colonnes sur iPad. Posé
   * ici, il passe À LA LIGNE au lieu d'être coupé, exactement le mécanisme
   * déjà documenté pour le delta.
   */
  secondary?: string
}

export function StatCard({ label, value, icon: Icon, delta, secondary }: StatCardProps) {
  // `up` = succès par défaut ; `tone` renverse la lecture quand monter est mauvais.
  const deltaTone = delta ? (delta.tone ?? (delta.direction === 'up' ? 'success' : 'danger')) : null

  return (
    <div className="flex items-center gap-2 rounded-card border border-border bg-background-primary p-3">
      {Icon && (
        // Tuile teintée : accent du produit, jamais une couleur par carte.
        // 32 px et non 36 : mesuré à 1024 px, barre latérale déployée, sur 4 colonnes,
        // une carte fait 179 px. Les pixels rendus au texte sont exactement ce qui
        // permet à une valeur suivie de son delta (« 82.4% ↑ 80% ») de tenir.
        <span
          aria-hidden="true"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-primary-500/10 text-primary-400"
        >
          <Icon className="h-5 w-5" />
        </span>
      )}
      {/* `min-w-0` : sans lui, la valeur longue empêche `truncate` d'agir et fait
          déborder la carte de sa colonne de grille. */}
      <div className="min-w-0">
        {/* Le libellé se REPLIE plutôt que d'être coupé. À 1024 px avec la barre
            latérale ouverte, une carte sur quatre fait 179 px : « Contacts
            intéressés » ou « Taux de conversion » y seraient tronqués à mi-mot, et
            un iPad n'a pas de survol pour révéler la suite. Deux lignes coûtent
            14 px à la rangée entière ; une étiquette illisible coûte l'information. */}
        <p className="text-t6 uppercase text-foreground-secondary">{label}</p>
        {/* `flex-wrap` : dans la colonne la plus étroite du produit (179 px), une
            valeur longue accompagnée d'un delta peut manquer de quelques pixels. Le
            delta passe alors À LA LIGNE au lieu de faire tronquer le chiffre — une
            carte un peu plus haute reste lisible, un « 82… » ne l'est plus. */}
        <p className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-t2 text-foreground tabular-nums">
          <span className="truncate">{value}</span>
          {/* `min-w-0` et PAS `shrink-0` (retouche recette dons) : la précision
              passe d'abord à la ligne sous la valeur ; si, seule sur sa ligne,
              elle est encore trop large (« · 15 septembre 2026 », 126 px dans
              115 sur la fiche à 1024 px), elle se REPLIE aux espaces au lieu
              de déborder de la carte. Jamais tronquée : la date est l'info. */}
          {secondary && (
            <span className="min-w-0 text-t5 text-foreground-secondary tabular-nums">
              {secondary}
            </span>
          )}
          {delta && (
            <span
              className={`shrink-0 text-t5 tabular-nums ${
                deltaTone === 'success' ? 'text-success-400' : 'text-danger-400'
              }`}
            >
              {delta.direction === 'up' ? '↑' : '↓'} {delta.value}
            </span>
          )}
        </p>
      </div>
    </div>
  )
}

/**
 * Classes de grille par NOMBRE de cartes.
 *
 * Règle du lot : **à chaque point de rupture, le nombre de colonnes divise le
 * nombre de cartes.** L'audit (§6.2 n°5) relevait `/contacts` à 5 cartes sur 4
 * colonnes, `/campaigns` et `/admin/realtime-stats` à 4 sur 3 : une carte seule
 * sur la dernière ligne, avec les trois quarts vides.
 *
 * Les comptes premiers > 3 (5, 7) ne sont volontairement PAS dans la table :
 * aucune grille à plusieurs colonnes ne peut les remplir. Ils retombent sur une
 * colonne unique — moche, donc visible, donc corrigé à la source en fusionnant
 * ou en complétant la métrique. Une carte orpheline ne peut plus revenir en
 * silence.
 *
 * ── RETOUCHE LOT-RT-DA1 (DA-012) ──────────────────────────────────────────
 *
 * La recette DA a mesuré deux défauts que la table d'origine laissait passer :
 *
 * 1. **Six cartes tenaient sur DEUX rangées.** `/contacts` — la page la plus
 *    chargée — rendait ses 6 cartes en 3 colonnes de 287 px à 1180 px : deux
 *    rangées, 148 px de haut, sur un écran où le premier chiffre arrivait déjà
 *    à 250 px. Au-delà de `min-[1100px]`, les 6 cartes passent donc sur UNE
 *    rangée de 6 colonnes. 6 divise 6 : la règle tient toujours.
 * 2. **La même primitive variait de 206 à 287 px selon la page** — 287 px pour
 *    un nombre à cinq chiffres, c'est une carte vide aux deux tiers. Chaque
 *    grille est désormais BORNÉE en largeur : `MAX_CARD_PX` par carte, plus les
 *    gouttières. Au-delà, la bande cesse de grandir au lieu d'étirer ses cartes.
 *
 * Le coût assumé de la borne : à très large viewport la bande ne va plus
 * jusqu'au bord droit du contenu. C'est cohérent avec le §5bis — « une rangée de
 * KPI est une BANDE d'information au-dessus du contenu, pas un panneau » : une
 * bande a une densité, pas une laisse.
 *
 * `min-[1100px]` est une variante ARBITRAIRE, pas un point de rupture nouveau :
 * ajouter un 6e `screens` à `tailwind.config.js` l'aurait rendu disponible
 * partout, alors qu'il ne concerne que ce cas.
 */

/**
 * Largeurs de bande, en toutes lettres.
 *
 * ⚠️ Ces classes ne peuvent PAS être composées à l'exécution
 * (`max-w-[${n}px]`) : le scanner de Tailwind lit des chaînes statiques dans le
 * source, une classe assemblée par gabarit n'est jamais générée et la borne
 * n'existerait tout simplement pas dans le CSS. Elles sont donc écrites en
 * clair, et `MAX_CARD_PX` / `GAP_PX` ne servent qu'à documenter — et à tester —
 * le calcul qui les produit :
 *
 *     bande(n) = n × 220 px + (n − 1) × 12 px
 *     1 → 220   2 → 452   3 → 684   4 → 916   6 → 1380
 */
/** Largeur maximale d'une carte. Au-delà, elle est vide plus qu'elle n'est lisible. */
export const MAX_CARD_PX = 220
/** Gouttière de la grille — la valeur de `gap-3`, posée plus bas sur le conteneur. */
export const GAP_PX = 12

const GRID_COLUMNS: Record<number, string> = {
  1: 'grid-cols-1 max-w-[220px]',
  2: 'grid-cols-2 max-w-[452px]',
  3: 'grid-cols-1 max-w-[220px] sm:grid-cols-3 sm:max-w-[684px]',
  4: 'grid-cols-2 max-w-[452px] lg:grid-cols-4 lg:max-w-[916px]',
  6: 'grid-cols-2 max-w-[452px] lg:grid-cols-3 lg:max-w-[684px] min-[1100px]:grid-cols-6 min-[1100px]:max-w-[1380px]',
  8: 'grid-cols-2 max-w-[452px] lg:grid-cols-4 lg:max-w-[916px]',
  9: 'grid-cols-3 max-w-[684px]',
}

/** Repli des comptes hors table (5, 7, 10…) : une colonne unique, bornée. */
const GRID_FALLBACK = 'grid-cols-1 max-w-[220px]'

export interface StatCardGridProps {
  children: ReactNode
  /** Classes de marge/position ajoutées au conteneur. Jamais de couleur ni de fond. */
  className?: string
}

/**
 * Rangée de `StatCard`. Choisit ses colonnes d'après le nombre d'enfants —
 * la page ne décide plus de sa propre grille, donc ne peut plus se tromper.
 */
export function StatCardGrid({ children, className = '' }: StatCardGridProps) {
  // `toArray` et non `count` : il écarte `null`, `undefined` et les booléens, donc
  // une carte conditionnelle (`{peutVoirLesCoûts && <StatCard …/>}`) qui ne rend
  // rien ne compte pas pour une colonne. `count`, lui, l'aurait comptée et aurait
  // recréé un trou dans la grille — exactement le défaut que ce lot supprime.
  const columns = GRID_COLUMNS[Children.toArray(children).length] ?? GRID_FALLBACK
  return <div className={`grid gap-3 ${columns} ${className}`.trim()}>{children}</div>
}

export default StatCard
